import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
	FiArrowLeft,
	FiUser,
	FiMail,
	FiPhone,
	FiCalendar,
	FiFileText,
	FiEdit3,
	FiCheck,
	FiX,
	FiClock,
	FiDownload,
	FiRefreshCw,
	FiHash,
	FiCopy,
	FiCheckCircle,
} from 'react-icons/fi';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import Label from '../../components/ui/label';
import SublyApi from '../../helpers/Api';
import FormResponsesDisplay from './FormResponsesDisplay';
import ExportModal from './components/ExportModal';

const SubmissionDetail = () => {
	const history = useHistory();
	const location = useLocation();
	const { id } = useParams();
	const token = useSelector((state) => state.admin.token);

	// Temporary: Use hardcoded admin token for testing
	const testToken =
		'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhZG1pbiI6InBlYW51dCIsImlzQXBwcm92ZWQiOnRydWUsImlhdCI6MTY0OTM4OTY0NX0.HhdqAZHDs0gCryngCOVCsZyqqk9xqeLmgwgkY5P7LqM';
	const actualToken = token || testToken;

	// State for submission data
	const [submission, setSubmission] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	// State for status update
	const [updatingStatus, setUpdatingStatus] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState('');
	const [statusOptions, setStatusOptions] = useState([]);

	// State for export modal
	const [exportModalOpen, setExportModalOpen] = useState(false);

	// Fetch submission details
	const fetchSubmission = async () => {
		setLoading(true);
		setError('');

		try {
			console.log('🔍 Fetching submission:', id);
			const response = await SublyApi.request(
				`api/admin/forms/submissions/${id}`,
				{},
				'get',
				{ Authorization: `Bearer ${actualToken}` }
			);

			console.log('✅ Submission data:', response);
			setSubmission(response.submission || response);
			setSelectedStatus(
				response.submission?.status || response?.status || 'submitted'
			);
		} catch (err) {
			console.error('❌ Error fetching submission:', err);
			const errorMessage =
				err?.message ||
				err?.error?.message ||
				err?.toString() ||
				'Failed to fetch submission details';
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// Fetch status options
	const fetchStatusOptions = async () => {
		try {
			const response = await SublyApi.request(
				'api/admin/forms/submission-statuses',
				{},
				'get',
				{ Authorization: `Bearer ${actualToken}` }
			);
			setStatusOptions(response.statuses || []);
		} catch (err) {
			console.error('❌ Error fetching status options:', err);
			// Fallback to hardcoded options if API fails
			setStatusOptions([
				{
					value: 'submitted',
					label: 'Submitted',
					color: 'bg-blue-100 text-blue-800 border-blue-200',
				},
				{
					value: 'in-review',
					label: 'In Review',
					color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
				},
				{
					value: 'tasked',
					label: 'Tasked',
					color: 'bg-green-100 text-green-800 border-green-200',
				},
			]);
		}
	};

	// Update submission status
	const handleStatusUpdate = async (newStatus) => {
		if (!submission || newStatus === submission.status) return;

		setUpdatingStatus(true);
		try {
			console.log('🔍 Updating status to:', newStatus);
			await SublyApi.request(
				`api/admin/forms/submissions/${id}/status`,
				{ status: newStatus },
				'put',
				{ Authorization: `Bearer ${actualToken}` }
			);

			// Update local state
			setSubmission((prev) => ({ ...prev, status: newStatus }));
			setSelectedStatus(newStatus);
			console.log('✅ Status updated successfully');
		} catch (err) {
			console.error('❌ Error updating status:', err);
			setError('Failed to update submission status');
		} finally {
			setUpdatingStatus(false);
		}
	};

	// Handle data update from FormResponsesDisplay
	const handleDataUpdate = (updatedData) => {
		setSubmission((prev) => ({
			...prev,
			data: updatedData,
		}));
	};

	// Smart back button that respects where user came from
	const handleBackNavigation = () => {
		const urlParams = new URLSearchParams(location.search);
		const from = urlParams.get('from');

		if (from === 'notifications') {
			history.push('/admin/notifications');
		} else {
			// Default to submissions page or use history.goBack()
			history.push('/admin/submissions');
		}
	};

	// Get back button text based on referrer
	const getBackButtonText = () => {
		const urlParams = new URLSearchParams(location.search);
		const from = urlParams.get('from');

		if (from === 'notifications') {
			return 'Back to Notifications';
		} else {
			return 'Back to Submissions';
		}
	};

	// Initial fetch
	useEffect(() => {
		if (id && actualToken) {
			fetchSubmission();
			fetchStatusOptions();
		}
	}, [id, actualToken]);

	// Format date for display
	const formatDate = (dateString) => {
		try {
			return new Date(dateString).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		} catch (error) {
			return 'Invalid Date';
		}
	};

	// Get status badge color and icon
	const getStatusInfo = (status) => {
		switch (status?.toLowerCase()) {
			case 'submitted':
				return {
					color: 'bg-blue-100 text-blue-800 border-blue-200',
					icon: <FiFileText className='h-4 w-4' />,
				};
			case 'in-review':
				return {
					color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
					icon: <FiClock className='h-4 w-4' />,
				};
			case 'tasked':
				return {
					color: 'bg-green-100 text-green-800 border-green-200',
					icon: <FiCheck className='h-4 w-4' />,
				};
			default:
				return {
					color: 'bg-gray-100 text-gray-800 border-gray-200',
					icon: <FiFileText className='h-4 w-4' />,
				};
		}
	};

	if (loading) {
		return (
			<div className='container mx-auto px-4 py-8'>
				<div className='flex justify-center items-center h-64'>
					<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
				</div>
			</div>
		);
	}

	if (error && !submission) {
		return (
			<div className='container mx-auto px-4 py-8'>
				<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6'>
					<p className='font-bold'>Error:</p>
					<p>{error}</p>
				</div>
				<Button variant='outline' onClick={handleBackNavigation}>
					<FiArrowLeft className='h-4 w-4 mr-2' />
					{getBackButtonText()}
				</Button>
			</div>
		);
	}

	if (!submission) {
		return (
			<div className='container mx-auto px-4 py-8'>
				<div className='text-center py-12'>
					<FiFileText className='h-12 w-12 text-gray-400 mx-auto mb-4' />
					<h3 className='text-lg font-medium text-gray-900 mb-2'>
						Submission not found
					</h3>
					<p className='text-gray-500 mb-4'>
						The requested submission could not be found.
					</p>
					<Button variant='outline' onClick={handleBackNavigation}>
						<FiArrowLeft className='h-4 w-4 mr-2' />
						{getBackButtonText()}
					</Button>
				</div>
			</div>
		);
	}

	const statusInfo = getStatusInfo(submission.status);

	return (
		<div className='container mx-auto px-4 py-8'>
			{/* Header */}
			<div className='flex items-center justify-between mb-6'>
				<div className='flex items-center'>
					<Button
						variant='ghost'
						onClick={handleBackNavigation}
						className='mr-4'>
						<FiArrowLeft className='h-4 w-4 mr-2' />
						{getBackButtonText()}
					</Button>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>
							Submission Details
						</h1>
						<p className='text-sm text-gray-500 mt-1'>
							Submission ID: {submission.id}
						</p>
					</div>
				</div>

				<div className='flex items-center space-x-2'>
					<Button
						variant='outline'
						onClick={fetchSubmission}
						disabled={loading}>
						<FiRefreshCw
							className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
						/>
						Refresh
					</Button>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6'>
					<p className='font-bold'>Error:</p>
					<p>{error}</p>
				</div>
			)}

			<div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
				{/* Main Content */}
				<div className='lg:col-span-2 space-y-6'>
					{/* Submission Information */}
					<Card>
						<Card.Header>
							<h2 className='text-lg font-medium'>Submission Information</h2>
						</Card.Header>
						<Card.Content>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
								{/* Form Information */}
								<div>
									<Label className='text-sm font-medium text-gray-700'>
										Form
									</Label>
									<div className='flex items-center mt-1'>
										<FiFileText className='h-4 w-4 text-gray-400 mr-2' />
										<p className='text-gray-900 font-medium'>
											{submission.form_name}
										</p>
									</div>
								</div>

								{/* Submitted At */}
								<div>
									<Label className='text-sm font-medium text-gray-700'>
										Submitted At
									</Label>
									<div className='flex items-center mt-1'>
										<FiCalendar className='h-4 w-4 text-gray-400 mr-2' />
										<p className='text-gray-900'>
											{formatDate(submission.submitted_at)}
										</p>
									</div>
								</div>

								{/* Submitted For */}
								<div>
									<Label className='text-sm font-medium text-gray-700'>
										Submitted For
									</Label>
									<div className='flex items-center mt-1'>
										<FiUser className='h-4 w-4 text-gray-400 mr-2' />
										<button
											onClick={() =>
												history.push(
													`/admin/clients/client-details/${submission.username}`
												)
											}
											className='text-blue-600 hover:text-blue-800 hover:underline font-medium'>
											{submission.first_name} {submission.last_name}
										</button>
									</div>
								</div>

								{/* Submitted By */}
								<div>
									<Label className='text-sm font-medium text-gray-700'>
										Submitted By
									</Label>
									<div className='flex items-center mt-1'>
										<FiUser className='h-4 w-4 text-gray-400 mr-2' />
										<p className='text-gray-900'>
											{submission.submitter_first_name &&
											submission.submitter_last_name
												? `${submission.submitter_first_name} ${submission.submitter_last_name}`
												: submission.submitted_by || 'Unknown'}
										</p>
									</div>
								</div>
							</div>
						</Card.Content>
					</Card>

					{/* Form Data */}
					<Card>
						<Card.Header>
							<h2 className='text-lg font-medium'>Form Responses</h2>
						</Card.Header>
						<Card.Content>
							<FormResponsesDisplay
								submission={submission}
								onDataUpdate={handleDataUpdate}
								token={actualToken}
							/>
						</Card.Content>
					</Card>
				</div>

				{/* Sidebar */}
				<div className='space-y-6'>
					{/* Status Management */}
					<Card>
						<Card.Header>
							<h2 className='text-lg font-medium'>Status Management</h2>
						</Card.Header>
						<Card.Content>
							<div className='space-y-4'>
								{/* Current Status */}
								<div className='flex items-center'>
									<Label className='text-sm font-medium text-gray-700 mr-2'>
										Current Status:
									</Label>
									<div
										className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
										{statusInfo.icon}
										<span className='ml-2'>
											{submission.status || 'submitted'}
										</span>
									</div>
								</div>

								{/* Status Update */}
								<div>
									<Label className='text-sm font-medium text-gray-700 mb-2'>
										Update Status:
									</Label>
									<div className='space-y-2'>
										{statusOptions.map((option) => (
											<button
												key={option.value}
												onClick={() => handleStatusUpdate(option.value)}
												disabled={
													updatingStatus || option.value === submission.status
												}
												className={`w-full text-left px-3 py-2 rounded border transition-colors ${
													option.value === submission.status
														? 'bg-gray-100 text-gray-500 cursor-not-allowed'
														: 'bg-white hover:bg-gray-50 text-gray-900 border-gray-300'
												}`}>
												<div className='flex items-center'>
													{getStatusInfo(option.value).icon}
													<span className='ml-2 capitalize'>
														{option.label.replace('-', ' ')}
													</span>
												</div>
											</button>
										))}
									</div>
								</div>

								{updatingStatus && (
									<div className='flex items-center justify-center py-2'>
										<div className='animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-900'></div>
										<span className='ml-2 text-sm text-gray-600'>
											Updating...
										</span>
									</div>
								)}
							</div>
						</Card.Content>
					</Card>

					{/* Quick Actions */}
					<Card>
						<Card.Header>
							<h2 className='text-lg font-medium'>Quick Actions</h2>
						</Card.Header>
						<Card.Content>
							<div className='space-y-2'>
								<Button
									variant='outline'
									className='w-full justify-start'
									onClick={() => setExportModalOpen(true)}>
									<FiDownload className='h-4 w-4 mr-2' />
									Export Data
								</Button>
							</div>
						</Card.Content>
					</Card>
				</div>
			</div>

			{/* Export Modal */}
			<ExportModal
				isOpen={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				submission={submission}
			/>
		</div>
	);
};

export default SubmissionDetail;
